'use client';

import { type CSSProperties, useEffect, useState } from 'react';
import Vinyl from '@/app/assets/imgs/vinyl.png';
import { AudioPauseIcon } from '@/app/components/icons/AudioPauseIcon';
import { AudioPlayIcon } from '@/app/components/icons/AudioPlayIcon';
import { Slider } from '@/app/components/ui/slider';
import { SITE, TEXT_2_AUDIO_IMAGE } from '@/app/constants';
import { useAudioPlay } from '@/app/hooks';
import { cn, formatTime, getAudioImageIndex } from '@/app/utils';
import { getBlurBackgroundImg } from '@/app/utils/client';

interface StudioAudioPlayerProps {
  url: string;
  id: string;
  duration?: number;
  label: string;
}

export function StudioAudioPlayer({ url, id, duration = 0, label }: StudioAudioPlayerProps) {
  const { isPlaying, progress, handlePlayPause, setProgress } = useAudioPlay({ url, id });
  const sliderMax = Math.max(duration, progress, 0.01);
  const audioImage = TEXT_2_AUDIO_IMAGE[getAudioImageIndex(id, TEXT_2_AUDIO_IMAGE.length)];
  const [blurBackgroundImage, setBlurBackgroundImage] = useState('');

  useEffect(() => {
    setBlurBackgroundImage(getBlurBackgroundImg(audioImage.blurHash, 0.1));
  }, [audioImage.blurHash]);

  return (
    <div
      className="relative isolate flex items-center gap-5 overflow-hidden rounded-16 border border-system-white08 bg-cover bg-no-repeat p-3"
      style={{ backgroundImage: blurBackgroundImage ? `url(${blurBackgroundImage})` : '' }}
    >
      <button
        type="button"
        onClick={handlePlayPause}
        aria-label={label}
        className="relative z-10 size-16 shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01"
      >
        <span className="absolute inset-0 z-[1] overflow-hidden rounded-10">
          <img src={audioImage.cover[SITE]} alt="" className="size-full object-cover" />
        </span>
        <img
          src={Vinyl.src}
          alt=""
          className={cn(
            'absolute inset-0 z-0 size-full animate-spin-vinyl opacity-0 transition-[left,opacity] motion-reduce:animate-none motion-reduce:transition-none',
            isPlaying && 'left-6 opacity-100',
          )}
        />
      </button>
      <div className="relative z-10 min-w-0 flex-1">
        <p className="mb-2 truncate text-left font-medium text-sm text-system-white">{label}</p>
        <div className="flex items-center gap-3">
          <span className="font-normal text-system-white/40 text-xs tabular-nums">{formatTime(progress)}</span>
          <Slider
            aria-label={label}
            value={[progress]}
            max={sliderMax}
            step={0.01}
            className="flex-1"
            onValueChange={(values) => {
              const nextProgress = values[0];
              if (nextProgress !== undefined) setProgress(nextProgress);
            }}
            style={
              {
                '--slider-track-color': 'rgba(255, 255, 255, 0.12)',
              } as CSSProperties
            }
          />
          <span className="font-normal text-system-white/40 text-xs tabular-nums">{formatTime(duration)}</span>
        </div>
        <button
          type="button"
          onClick={handlePlayPause}
          aria-label={label}
          className="mt-2 flex h-5 w-10 items-center justify-center text-system-white transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 motion-reduce:transition-none"
        >
          {isPlaying ? <AudioPauseIcon className="h-full w-full" /> : <AudioPlayIcon className="h-full w-full" />}
        </button>
      </div>
    </div>
  );
}
